'use client';
import React, { useEffect, useRef } from 'react';
import { Canvas, useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three/src/loaders/TextureLoader';
import styles from './style.module.scss';
import { OrbitControls, SpotLight } from '@react-three/drei';
import { useSpring, useScroll, useTransform, useMotionValue } from 'framer-motion';
import { motion } from 'framer-motion-3d';

import image1 from '../img/1.jpg';
import image2 from '../img/6.jpg';
import image3 from '../img/10.jpg';
import image4 from '../img/kamla.jpg';
import image5 from '../img/ghostoftsushima.jpg';
import image6 from '../img/Inside.jpg';

export default function Cube() {

    const container = useRef(null);
    const { scrollYProgress } = useScroll({
        target: container,
        offset: ['start start', 'end end']
    });

    const progress = useTransform(scrollYProgress, [0, 1], [0, 5]);
    const smoothProgress = useSpring(progress, { damping: 30 });

    return (
        <div ref={container} className={styles.main3}>
            <div className={styles.cube}>
                <Canvas>
                    <OrbitControls enableZoom={false} enablePan={false} />
                    <ambientLight intensity={1.5} />
                    <directionalLight position={[1, 2, 1]} />
                    <SpotLight
                        position={[10, 10, 10]}
                        angle={1}
                        penumbra={1}
                        intensity={2}
                        castShadow
                    />
                    <CubeModel scrollProgress={smoothProgress} />
                </Canvas>
            </div>
        </div>
    );
}

function CubeModel({ scrollProgress }) {

    const mesh = useRef(null);

    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    useEffect(() => {
        const handleMouseMove = (e) => {
            const { innerWidth, innerHeight } = window;
            const { clientX, clientY } = e;
            const x = (clientX / innerWidth) * 2 - 1;
            const y = (clientY / innerHeight) * 2 - 1;
            mouseX.set(x * 0.5);
            mouseY.set(-y * 0.5);
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [mouseX, mouseY]);

    const texture_1 = useLoader(TextureLoader, image1);
    const texture_2 = useLoader(TextureLoader, image2);
    const texture_3 = useLoader(TextureLoader, image3);
    const texture_4 = useLoader(TextureLoader, image4);
    const texture_5 = useLoader(TextureLoader, image5);
    const texture_6 = useLoader(TextureLoader, image6);

    const combinedRotationY = useSpring(useTransform([scrollProgress, mouseX], ([s, m]) => s + m), { damping: 30 });
    const combinedRotationX = useSpring(useTransform([scrollProgress, mouseY], ([s, m]) => s + m), { damping: 30 });

    return (
        <motion.mesh ref={mesh} rotation-y={combinedRotationY} rotation-x={combinedRotationX}>
            <boxGeometry args={[3, 3, 3]} />
            <meshStandardMaterial map={texture_1} attach="material-0" />
            <meshStandardMaterial map={texture_2} attach="material-1" />
            <meshStandardMaterial map={texture_3} attach="material-2" />
            <meshStandardMaterial map={texture_4} attach="material-3" />
            <meshStandardMaterial map={texture_5} attach="material-4" />
            <meshStandardMaterial map={texture_6} attach="material-5" />
        </motion.mesh>
    );
}
